import { get, isEmpty } from 'lodash';
import type { ThunkDispatch } from 'redux-thunk';

import type { IAccount } from '@share/common-types';
import { SignUpPublicUser } from '@share/services';
import { establishMemberSessionFromSignup } from '@share/store/slices';
import type { AppThunk, FunctionalRootState } from '@share/utils';
import { normalizePublicSignupUser } from '@share/utils';

import type { PaymentFormValues } from '../types';

export const PUBLIC_SIGNUP_EMAIL_IN_USE_CODE = 'email_in_use';

export type PublicClubSiteCheckoutSignupResult =
  | { ok: true }
  | { ok: false; message: string };

function readErrorPayload(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return data === PUBLIC_SIGNUP_EMAIL_IN_USE_CODE ? { reason: data } : null;
    }
  }

  if (data && typeof data === 'object') {
    return data as Record<string, unknown>;
  }

  return null;
}

function resolvePublicSignupErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }

  const payload = readErrorPayload((error as { response?: { data?: unknown } }).response?.data);
  if (!payload) {
    return null;
  }

  const candidates = [payload.reason, payload.code, payload.error, payload.type];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate === PUBLIC_SIGNUP_EMAIL_IN_USE_CODE) {
      return PUBLIC_SIGNUP_EMAIL_IN_USE_CODE;
    }
  }

  return null;
}

function parsePublicSignupError(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }

  const payload = readErrorPayload((error as { response?: { data?: unknown } }).response?.data);
  if (!payload) {
    return null;
  }

  const reason = get(payload, 'reason', null);
  if (typeof reason === 'string' && !isEmpty(reason) && reason !== PUBLIC_SIGNUP_EMAIL_IN_USE_CODE) {
    return reason;
  }

  const message = get(payload, 'message', null);
  if (typeof message === 'string' && !isEmpty(message)) {
    return message;
  }

  return null;
}

export async function registerPublicClubSiteMemberSession({
  dispatch,
  account,
  values,
  signupFailedMessage,
  emailInUseMessage,
}: {
  dispatch: ThunkDispatch<FunctionalRootState, unknown, AppThunk>;
  account: IAccount;
  values: Pick<PaymentFormValues, 'firstName' | 'lastName' | 'email' | 'password'>;
  signupFailedMessage: string;
  emailInUseMessage: string;
}): Promise<PublicClubSiteCheckoutSignupResult> {
  try {
    const email = values.email.trim();
    const response = await SignUpPublicUser({
      email,
      password: values.password,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
    });

    const user = response?.data ? normalizePublicSignupUser(response.data) : null;
    if (response?.status !== 200 || !user) {
      return { ok: false, message: signupFailedMessage };
    }

    await dispatch(establishMemberSessionFromSignup(user));
    return { ok: true };
  } catch (error: unknown) {
    if (resolvePublicSignupErrorCode(error) === PUBLIC_SIGNUP_EMAIL_IN_USE_CODE) {
      return { ok: false, message: emailInUseMessage };
    }

    const parsed = parsePublicSignupError(error);
    if (error instanceof Error && !parsed && error.message) {
      return { ok: false, message: error.message };
    }
    return { ok: false, message: parsed ?? signupFailedMessage };
  }
}
